/**
 * Klaviyo notify form:
 * - Back in stock: Inline form, submits to POST /client/back-in-stock-subscriptions/
 * - List (popup): Button only, opens Klaviyo popup via _klOnsite.openForm (captures email + SMS)
 */
class KlaviyoNotifyForm extends HTMLElement {
  static get observedAttributes() {
    return ['data-company-id', 'data-subscription-mode', 'data-list-id', 'data-list-form-id', 'data-variant-ids'];
  }

  connectedCallback() {
    const popupTrigger = this.querySelector('[data-klaviyo-popup-trigger]');
    if (popupTrigger) {
      popupTrigger.addEventListener('click', this.openListPopup.bind(this));
      return;
    }

    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  openListPopup(e) {
    e.preventDefault();
    const formId = (this.dataset.listFormId || '').trim();
    if (!formId) {
      console.warn('Klaviyo List Form: Form ID not set. Add Klaviyo List Form ID in the Notify When Available block settings.');
      return;
    }
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  }

  async handleSubmit(e) {
    e.preventDefault();
    const errorEl = this.querySelector('[data-notify-error]');
    if (errorEl) errorEl.classList.remove('is-visible');

    const companyId = (this.dataset.companyId || '').trim();
    if (!companyId) {
      this.showError('Klaviyo is not configured. Add your Company ID in the block settings.');
      return;
    }

    const submitBtn = this.form.querySelector('[type="submit"]');
    const emailInput = this.form.querySelector('[name="contact[email]"]');
    const email = emailInput?.value?.trim();
    if (!email) return;

    const mode = (this.dataset.subscriptionMode || 'back_in_stock').trim();

    this.setLoading(true, submitBtn);

    try {
      if (mode === 'list') {
        await this.subscribeToList(companyId, email);
      } else {
        await this.subscribeBackInStock(companyId, email);
      }
      this.showSuccess();
    } catch (err) {
      this.showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      this.setLoading(false, submitBtn);
    }
  }

  async subscribeToList(companyId, email) {
    const listId = (this.dataset.listId || '').trim();
    if (!listId) {
      throw new Error('Klaviyo List ID is required. Add it in the block settings.');
    }

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: '2026-01-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Product notify list',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    subscriptions: {
                      email: {
                        marketing: { consent: 'SUBSCRIBED' },
                      },
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: listId,
                },
              },
            },
          },
        }),
      }
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const detail = body?.errors?.[0]?.detail || `Request failed (${response.status})`;
      throw new Error(detail);
    }
  }

  async subscribeBackInStock(companyId, email) {
    const variantIds = (this.dataset.variantIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    if (!variantIds.length) {
      throw new Error('No variants to subscribe to.');
    }

    for (const variantId of variantIds) {
      const klaviyoVariantId = `$shopify:::$default:::${variantId}`;
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: '2026-01-15',
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email },
                  },
                },
                channels: ['EMAIL'],
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: klaviyoVariantId,
                  },
                },
              },
            },
          }),
        }
      );

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const detail = body?.errors?.[0]?.detail || `Request failed (${response.status})`;
        throw new Error(detail);
      }
    }
  }

  setLoading(loading, btn) {
    if (!btn) return;
    btn.disabled = loading;
    btn.dataset.loading = loading ? 'true' : '';
  }

  showSuccess() {
    const successEl = this.querySelector('[data-notify-success]');
    const formWrap = this.querySelector('[data-notify-form-wrap]');
    const errorEl = this.querySelector('[data-notify-error]');
    if (successEl) successEl.classList.add('is-visible');
    if (formWrap) formWrap.hidden = true;
    if (errorEl) errorEl.classList.remove('is-visible');
    const focusTarget = successEl?.querySelector('[tabindex="-1"]') || successEl;
    focusTarget?.focus?.();
  }

  showError(message) {
    const errorEl = this.querySelector('[data-notify-error]');
    if (errorEl) {
      const text = errorEl.querySelector('[data-notify-error-text]');
      if (text) text.textContent = message;
      else errorEl.textContent = message;
      errorEl.classList.add('is-visible');
      errorEl.focus?.();
    }
  }
}

customElements.define('klaviyo-notify-form', KlaviyoNotifyForm);
